import React, { useState, useEffect, useCallback } from 'react';

/**
 * Contractor Reply Triage Panel — 2026-06-05
 *
 * Opens when the agent clicks a `contractor_reply` realtime alert.
 * Shows the contractor's reply, the prior outbound, the maintenance
 * context, and lets the agent reply / accept the quote / dismiss —
 * all without leaving the dashboard.
 *
 * Props: outreachId (str), onClose (fn), onActioned? (fn).
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ORANGE = '#ea580c';
const ORANGE_DARK = '#9a3412';

function stripHtml(s) {
  if (!s) return '';
  let t = String(s)
    .replace(/<[^>]+>/g, ' ')           // tags
    .replace(/&#65279;|&#xFEFF;/g, '')   // zero-width BOM
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'");

  // Drop everything from common email "quoted history" markers.
  const cutMarkers = [
    /\n\s*On\s+\d.+?wrote:[\s\S]*$/i,           // "On 8 Jun 2026, at 20:54, X wrote:"
    /\n\s*-{2,}\s*Original\s+Message\s*-{2,}[\s\S]*$/i,
    /\n\s*From:\s+.+\n[\s\S]*$/i,                 // outlook headers
    /\n\s*Sent from my (iPhone|iPad|Samsung|mobile)[\s\S]*$/i,
    /\n\s*Get Outlook for (iOS|Android)[\s\S]*$/i,
  ];
  for (const r of cutMarkers) t = t.replace(r, '');
  return t.replace(/\s+/g, ' ').trim();
}

export default function ContractorReplyTriage({ outreachId, onClose, onActioned }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const reload = useCallback(async () => {
    if (!outreachId) return;
    setLoading(true); setErr(null);
    try {
      const res = await fetch(`${API}/maintenance/contractor-thread/${outreachId}`,
        { credentials: 'include' });
      if (!res.ok) {
        const t = await res.text();
        setErr(t || 'Could not load contractor thread.');
        return;
      }
      setData(await res.json());
    } finally {
      setLoading(false);
    }
  }, [outreachId]);

  useEffect(() => { reload(); }, [reload]);

  const sendReply = async () => {
    if (!reply.trim()) { setErr('Reply cannot be empty.'); return; }
    setSending(true); setErr(null); setSent(false);
    try {
      const res = await fetch(
        `${API}/maintenance/contractor-thread/${outreachId}/reply`,
        {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ body: reply }),
        },
      );
      if (!res.ok) {
        const t = await res.text();
        setErr(t || 'Send failed.');
        return;
      }
      setSent(true);
      setReply('');
      await reload();
      if (onActioned) onActioned();
    } finally {
      setSending(false);
    }
  };

  const acceptQuote = async () => {
    // Item 8 — Refuse if another contractor on the same MR has already
    // been approved. The backend enforces this too (409), but we surface
    // a clearer prompt up-front.
    if (data && data.mr_coord_approved_outreach_id
        && data.mr_coord_approved_outreach_id !== outreachId) {
      window.alert(
        'Another contractor has already been approved for this job. '
        + 'Only one contractor can be confirmed per maintenance request.'
      );
      return;
    }
    const promptMsg = (data && data.coord_proposed_visit_text)
      ? (`Approve ${data.contractor.company_name}'s quote for ${data.contractor_ref}?\n\n`
         + `Proposed visit: ${data.coord_proposed_visit_text}\n\n`
         + 'This will ask the tenant to confirm the visit. '
         + 'No other contractor can be approved afterwards.')
      : (`Approve ${data.contractor.company_name}'s quote and mark ${data.contractor_ref} as contractor-assigned?\n\n`
         + 'No proposed date was parsed from their reply — the tenant '
         + 'will not be emailed automatically. Use Send reply above to '
         + 'request a specific date if needed.');
    if (!window.confirm(promptMsg)) return;
    setAccepting(true); setErr(null); setAccepted(false);
    try {
      const res = await fetch(
        `${API}/maintenance/contractor-thread/${outreachId}/accept`,
        {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: null }),
        },
      );
      if (!res.ok) {
        const t = await res.text();
        setErr(t || 'Failed.');
        return;
      }
      setAccepted(true);
      // Item 6 — refresh sidebar badge counts on approval.
      try { window.dispatchEvent(new Event('eiq-alerts-refresh')); } catch { /* noop */ }
      if (onActioned) onActioned();
    } finally {
      setAccepting(false);
    }
  };

  const insertTemplate = (key) => {
    if (!data) return;
    const name = (data.contractor.contact_name || '').split(' ')[0] || 'there';
    const tmap = {
      accept: `Hi ${name},\n\nThanks for the quote — please go ahead and book in the work. Tenant contact details to follow shortly.\n\nKind regards.`,
      followup: `Hi ${name},\n\nThanks for getting back to me on ${data.contractor_ref}. Could you confirm:\n\n• When you can attend\n• Whether parts are included in the quote\n• Any access requirements\n\nKind regards.`,
      decline: `Hi ${name},\n\nThanks for the quote — we've decided to proceed with another contractor on this one. Appreciate your time.\n\nKind regards.`,
    };
    setReply(tmap[key] || '');
  };

  return (
    <div data-testid="contractor-reply-triage"
         style={{ position: 'fixed', inset: 0,
                  background: 'rgba(15,23,42,0.55)',
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'center', zIndex: 100,
                  padding: '3vh 1rem', overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: 14,
                    width: 'min(820px, 96vw)', maxHeight: '94vh',
                    display: 'flex', flexDirection: 'column',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        {/* Header */}
        <div style={{ padding: '1.1rem 1.4rem',
                      borderBottom: '1px solid #fed7aa',
                      background: '#fff7ed',
                      borderRadius: '14px 14px 0 0',
                      display: 'flex', justifyContent: 'space-between',
                      alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ color: ORANGE_DARK, fontSize: '0.72rem',
                          fontWeight: 700, textTransform: 'uppercase',
                          letterSpacing: '0.06em' }}>
              Contractor reply
            </div>
            <h3 data-testid="ctr-triage-title"
                style={{ margin: '4px 0 0', color: '#0f172a',
                         fontSize: '1.2rem' }}>
              {data?.contractor?.company_name || 'Contractor'}{' '}
              {data?.contractor_ref && (
                <span style={{ color: ORANGE, fontWeight: 700 }}>
                  · {data.contractor_ref}
                </span>
              )}
            </h3>
            {data && (
              <div style={{ color: '#475569', fontSize: '0.85rem',
                            marginTop: 4 }}>
                {data.category} at {data.postcode || data.property_address}
                {' · '}
                <span style={{ color: data.priority === 'emergency' ? '#dc2626' : '#0f172a' }}>
                  {data.priority}
                </span>
              </div>
            )}
          </div>
          <button onClick={onClose}
                  data-testid="ctr-triage-close"
                  style={{ background: 'transparent', border: 'none',
                           fontSize: '1.4rem', cursor: 'pointer',
                           color: '#0f172a', lineHeight: 1 }}>
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.2rem 1.4rem' }}>
          {loading && <div data-testid="ctr-triage-loading">Loading thread…</div>}
          {!loading && err && (
            <div data-testid="ctr-triage-err" style={{ color: '#b91c1c' }}>{err}</div>
          )}

          {!loading && data && (
            <>
              {/* Context strip */}
              <div data-testid="ctr-triage-context"
                   style={{ background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: 10,
                            padding: '0.85rem 1rem',
                            marginBottom: 14,
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: 8, fontSize: '0.85rem' }}>
                <div>
                  <strong style={{ color: '#475569' }}>Property</strong>
                  <div style={{ color: '#0f172a' }}>{data.property_address || '—'}</div>
                </div>
                <div>
                  <strong style={{ color: '#475569' }}>Tenant</strong>
                  <div style={{ color: '#0f172a' }}>{data.tenant_name || '—'}</div>
                </div>
                <div>
                  <strong style={{ color: '#475569' }}>Contractor</strong>
                  <div style={{ color: '#0f172a' }}>
                    {data.contractor.contact_name} · {data.contractor.email}
                    {data.contractor.is_inhouse && (
                      <span style={{ marginLeft: 6, background: '#dcfce7',
                                     color: '#166534', borderRadius: 999,
                                     padding: '1px 8px',
                                     fontSize: '0.7rem',
                                     fontWeight: 600 }}>
                        In-house
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <strong style={{ color: '#475569' }}>Status</strong>
                  <div style={{ color: '#0f172a' }}>
                    {data.maintenance_status}
                  </div>
                </div>
                {data.coord_proposed_visit_text && (
                  <div data-testid="ctr-triage-proposed" style={{ gridColumn: '1 / -1' }}>
                    <strong style={{ color: '#475569' }}>Proposed visit from this contractor</strong>
                    <div style={{ color: '#0f172a', fontWeight: 600 }}>
                      {data.coord_proposed_visit_text}
                    </div>
                  </div>
                )}
                {data.mr_coord_approved_outreach_id && data.mr_coord_approved_outreach_id !== outreachId && (
                  <div data-testid="ctr-triage-other-approved"
                       style={{ gridColumn: '1 / -1',
                                background: '#fef3c7',
                                border: '1px solid #fde68a',
                                color: '#92400e', padding: '0.5rem 0.7rem',
                                borderRadius: 8, fontSize: '0.82rem' }}>
                    A different contractor has already been approved for this job.
                    To prevent double-booking, this quote cannot be accepted.
                  </div>
                )}
                <div style={{ gridColumn: '1 / -1' }}>
                  <strong style={{ color: '#475569' }}>Original issue</strong>
                  <div style={{ color: '#0f172a' }}>{data.description}</div>
                </div>
              </div>

              {/* Thread */}
              <h4 style={{ margin: '0 0 8px', color: '#0f172a',
                           fontSize: '0.95rem' }}>
                Email thread
              </h4>
              {(data.thread || []).length === 0 && (
                <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
                  No emails recorded yet.
                </div>
              )}
              {(data.thread || []).map((msg, idx) => (
                <div key={idx}
                     data-testid={`ctr-triage-msg-${idx}`}
                     style={{ marginBottom: 10,
                              padding: '0.8rem 0.95rem',
                              borderRadius: 10,
                              background: msg.kind === 'inbound' ? '#fff7ed' : '#f1f5f9',
                              border: msg.kind === 'inbound' ? '1px solid #fed7aa' : '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between',
                                gap: 8, marginBottom: 4,
                                fontSize: '0.75rem', color: '#64748b' }}>
                    <span style={{ fontWeight: 700,
                                   color: msg.kind === 'inbound' ? ORANGE_DARK : '#475569' }}>
                      {msg.kind === 'inbound' ? `${data.contractor.company_name} →` : '→ Contractor'}
                    </span>
                    <span>{msg.sent_at ? new Date(msg.sent_at).toLocaleString() : ''}</span>
                  </div>
                  {msg.subject && (
                    <div style={{ fontSize: '0.85rem', fontWeight: 600,
                                  color: '#0f172a', marginBottom: 6 }}>
                      {msg.subject}
                    </div>
                  )}
                  <div style={{ fontSize: '0.88rem', color: '#0f172a',
                                whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
                    {stripHtml(msg.body_html).slice(0, 1200)}
                    {(msg.body_html || '').length > 2000 ? '…' : ''}
                  </div>
                  {msg.handyman_response && (
                    <div style={{ marginTop: 6, fontSize: '0.75rem',
                                  color: '#7c3aed', fontWeight: 700 }}>
                      Handyman replied: {msg.handyman_response.toUpperCase()}
                    </div>
                  )}
                </div>
              ))}

              {/* Reply composer */}
              <h4 style={{ margin: '14px 0 6px', color: '#0f172a',
                           fontSize: '0.95rem' }}>
                Reply to {data.contractor.company_name}
              </h4>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap',
                            marginBottom: 8 }}>
                <button onClick={() => insertTemplate('accept')}
                        data-testid="ctr-tmpl-accept"
                        style={tplBtnStyle}>Accept quote</button>
                <button onClick={() => insertTemplate('followup')}
                        data-testid="ctr-tmpl-followup"
                        style={tplBtnStyle}>Ask follow-up</button>
                <button onClick={() => insertTemplate('decline')}
                        data-testid="ctr-tmpl-decline"
                        style={tplBtnStyle}>Politely decline</button>
              </div>
              <textarea value={reply}
                        onChange={e => setReply(e.target.value)}
                        rows={7}
                        placeholder="Type your reply…"
                        data-testid="ctr-triage-reply"
                        style={{ width: '100%',
                                 padding: '0.75rem 0.9rem',
                                 border: '1px solid #cbd5e1',
                                 borderRadius: 10,
                                 fontFamily: 'inherit',
                                 fontSize: '0.9rem',
                                 resize: 'vertical' }} />

              {sent && (
                <div data-testid="ctr-triage-sent"
                     style={{ marginTop: 8, color: '#166534',
                              fontSize: '0.85rem' }}>
                  Reply sent from your mailbox.
                </div>
              )}
              {accepted && (
                <div data-testid="ctr-triage-accepted"
                     style={{ marginTop: 8, color: '#166534',
                              fontSize: '0.85rem' }}>
                  Quote approved — {data.coord_proposed_visit_text
                    ? 'tenant has been emailed to confirm the proposed visit. The calendar invite will go out only to this contractor once the tenant accepts.'
                    : 'maintenance ticket marked contractor-assigned. No other contractor can be approved for this job.'}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!loading && data && (
          <div style={{ borderTop: '1px solid #e2e8f0',
                        padding: '0.9rem 1.4rem',
                        display: 'flex', gap: 10,
                        justifyContent: 'flex-end',
                        background: '#fff' }}>
            <button onClick={acceptQuote}
                    disabled={accepting || (data && data.mr_coord_approved_outreach_id
                                              && data.mr_coord_approved_outreach_id !== outreachId)}
                    data-testid="ctr-triage-accept"
                    title={(data && data.mr_coord_approved_outreach_id
                             && data.mr_coord_approved_outreach_id !== outreachId)
                            ? 'Another contractor has already been approved for this job.'
                            : 'Approve this contractor and ask the tenant to confirm the visit.'}
                    style={{ background: '#16a34a', color: '#fff',
                             border: 'none',
                             padding: '0.55rem 1.1rem',
                             borderRadius: 8,
                             cursor: accepting ? 'not-allowed' : 'pointer',
                             fontWeight: 600,
                             opacity: accepting ? 0.7 : 1 }}>
              {accepting ? '…'
                : (data && data.mr_coord_approved_outreach_id === outreachId
                    ? 'Approved'
                    : 'Approve & confirm with tenant')}
            </button>
            <button onClick={sendReply}
                    disabled={sending || !reply.trim()}
                    data-testid="ctr-triage-send"
                    style={{ background: ORANGE, color: '#fff',
                             border: 'none',
                             padding: '0.55rem 1.3rem',
                             borderRadius: 8,
                             cursor: sending ? 'not-allowed' : 'pointer',
                             fontWeight: 600,
                             opacity: sending || !reply.trim() ? 0.7 : 1 }}>
              {sending ? 'Sending…' : 'Send reply'}
            </button>
            <button onClick={onClose}
                    data-testid="ctr-triage-cancel"
                    style={{ background: '#f1f5f9', color: '#0f172a',
                             border: 'none',
                             padding: '0.55rem 1.1rem',
                             borderRadius: 8, cursor: 'pointer' }}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const tplBtnStyle = {
  background: '#fff',
  border: '1px solid #cbd5e1',
  borderRadius: 999,
  padding: '0.3rem 0.85rem',
  fontSize: '0.8rem',
  cursor: 'pointer',
  color: '#475569',
};
